import { Grid, Paper, Typography } from "@mui/material";
import { FieldArray, Form, Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import Alert from "@material-ui/lab/Alert";
import history from "../../../../routes/History";
import {
  ADD_EDIT_FLASHCARD_SCHEMA,
  flashCardInitialObj,
  getInitialValuesAdd,
  getInitialValuesEdit
} from "../validation";
import Input from "../../../../components/Input";
import CustomSelect from "../../../../components/Select";
import InfiniteScrollSelect from "../../../../components/Select/InfiniteScrollSelect";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllFlashCardSubTopicsRequest,
  getAllFlashCardTopicsRequest
} from "../../../../store/FlashCard/Topics/actions";
import { formatOptions } from "../../../../utils/Helpers";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import RemoveIcon from "@material-ui/icons/Remove";
import { showHideSnackbar } from "../../../../store/Snackbar/actions";
import FlashCardAnswerForm from "./FlashCardAnswerForm";
import Button from "../../../../components/Button";
import { useIntl } from "react-intl";
import {
  addArrFlashCardRequest,
  editSingleFlashCardRequest,
  getSingleFlashCardRequest
} from "../../../../store/FlashCard/Questions/actions";
import { showHideLoader } from "../../../../store/Loader/actions";
import { uploadToS3 } from "../../../../utils/S3";
import FlashCardAnswerView from "./FlashCardAnswerView";
import MaterialPreviewCarousel from "../../../../components/MaterialPreview/MaterialPreviewCarousel";
import {
  DIFFIECULTY_LEVEL_LIST,
  MODE_ELIGIBILITY
} from "../../../../utils/Constants";

export const uploadType = {
  question: "question",
  justification: "justification",
  answer: "answer"
};
function FlashCardForm() {
  const { mode, id } = useParams();
  const location = useLocation();
  const isNotAddMode = mode !== "Add";
  const isViewMode = mode === "View";
  const formRef = useRef(null);
  const uploadQuestionRef = useRef(null);
  const uploadJustificationRef = useRef(null);

  // Smart Import: capture once on mount (Add mode only), then strip the
  // history state so navigating to Add again later starts clean.
  const [imported] = useState(
    () => (mode === "Add" && location.state?.smartImport) || null
  );
  useEffect(() => {
    if (location.state?.smartImport) {
      history.replace({
        pathname: location.pathname,
        search: location.search
      });
    }
    // Runs once on mount: the history state is stripped exactly once.
  }, []);
  const { flashcard } = useSelector(
    (state) => state.flashCardQuestions
  );
  const {
    list,
    meta: topicsMeta,
    hasMore: topicsHasMore,
    subTopicsList: currentSubTopicsList,
    subTopicMeta,
    subTopicsHasMore,
    currentSubTopicTopicId
  } = useSelector((state) => state.flashCardTopics);
  const dispatch = useDispatch();

  useEffect(() => {
    if (flashcard?.topic_id) {
      dispatch(
        getAllFlashCardSubTopicsRequest({
          topicId: flashcard.topic_id,
          page: 1,
          items: 10,
          paginate: true
        })
      );
    }
  }, [flashcard?.topic_id, flashcard?.id, dispatch]);
  useEffect(() => {
    dispatch(getAllFlashCardTopicsRequest({ page: 1, items: 10 }));
  }, []);
  useEffect(() => {
    if (id) dispatch(getSingleFlashCardRequest({ id }));
  }, [id]);
  const { messages } = useIntl();

  const {
    buttons: { submit },
    flashcard: {
      label: { questionLabel, explanationLabel, uploadImageLabel },
      questionCodeLabel
    }
  } = messages;
  const [subTopicsCache, setSubTopicsCache] = useState({});
  const topicsSearchRef = useRef("");

  // Sync Redux sub-topics response into per-topicId cache
  useEffect(() => {
    if (currentSubTopicTopicId && currentSubTopicsList) {
      setSubTopicsCache((prev) => ({
        ...prev,
        [currentSubTopicTopicId]: {
          ...(prev[currentSubTopicTopicId] || {}),
          items: [...currentSubTopicsList],
          hasMore: subTopicsHasMore,
          page: subTopicMeta?.currentPage || 1
        }
      }));
    }
  }, [
    currentSubTopicsList,
    currentSubTopicTopicId,
    subTopicsHasMore,
    subTopicMeta
  ]);

  const uploadAttachments = async (attachments = []) => {
    return Promise.all(
      attachments.map(async (att) => {
        if (att.path?.file instanceof File) {
          const { link } = await uploadToS3(
            "private",
            att.path.file,
            att.path.file.name
          );
          return { ...att, path: link };
        }
        return att;
      })
    );
  };

  const updatedQuestions = async (values) => {
    return await Promise.all(
      values.flashcards.map(async (question) => {
        const updatedQuestion = { ...question };

        // Handle question images array
        try {
          updatedQuestion.flashcard_attachments_attributes =
            await uploadAttachments(
              updatedQuestion.flashcard_attachments_attributes
            );
        } catch (err) {
          console.error("Failed to upload question images", err);
        }

        // Handle justification images array
        try {
          updatedQuestion.flashcard_explanation_attributes = {
            ...updatedQuestion.flashcard_explanation_attributes,
            flashcard_attachments_attributes: await uploadAttachments(
              updatedQuestion.flashcard_explanation_attributes
                ?.flashcard_attachments_attributes
            )
          };
        } catch (err) {
          console.error("Failed to upload justification images", err);
        }

        // Handle answers images array
        updatedQuestion.flashcard_options_attributes =
          await Promise.all(
            (question.flashcard_options_attributes || []).map(
              async (answer) => {
                const updatedAnswer = { ...answer };

                try {
                  if (updatedAnswer.currentTab !== "text") {
                    updatedAnswer.flashcard_attachments_attributes =
                      await uploadAttachments(
                        updatedAnswer.flashcard_attachments_attributes
                      );
                  }
                } catch (err) {
                  console.error(
                    "Failed to upload answer images",
                    err
                  );
                  updatedAnswer.flashcard_attachments_attributes = [];
                }

                return updatedAnswer;
              }
            )
          );

        if (updatedQuestion.mode_eligibility === "memory_only") {
          const options =
            updatedQuestion.flashcard_options_attributes || [];
          const destroyed = options.filter((o) => o._destroy && o.id);
          const active = options.filter((o) => !o._destroy);
          const provided = active.find(
            (o) =>
              `${o.content ?? ""}`.trim() !== "" ||
              (o.flashcard_attachments_attributes || []).some(
                (a) => !a._destroy
              )
          );
          const removedActive = active
            .filter((o) => o !== provided && o.id)
            .map((o) => ({ id: o.id, _destroy: true }));
          const finalOptions = [
            ...destroyed,
            ...removedActive,
            ...(provided ? [{ ...provided, correct: true }] : [])
          ];
          updatedQuestion.flashcard_options_attributes =
            finalOptions.length ? finalOptions : undefined;
        }

        delete updatedQuestion.without_options;
        // Smart Import review metadata is UI-only; never send it to the API.
        delete updatedQuestion.answer_source;
        // Codes are generated by the backend before saving; never send one.
        delete updatedQuestion.question_code;

        return updatedQuestion;
      })
    );
  };
  async function handleSubmit(value) {
    dispatch(showHideLoader(true));
    if (mode === "Add") {
      const newValues = await updatedQuestions(value);
      dispatch(addArrFlashCardRequest({ flashcards: newValues }));
    } else if (mode === "Edit") {
      const newValues = await updatedQuestions(value);
      dispatch(
        editSingleFlashCardRequest({
          flashcard: newValues?.[0],
          id: newValues?.[0]?.id
        })
      );
    }
  }

  const AddRemoveNewQuestion = (push) => {
    return (
      <>
        <div className="mx-2 mb-3">
          <AddCircleIcon
            className="cursor-pointer"
            onClick={() =>
              push({
                ...flashCardInitialObj,
                tempId: crypto.randomUUID()
              })
            }
          />
        </div>
      </>
    );
  };
  const RemoveFlashcard = (
    flashcard,
    remove,
    flashcardIndex,
    replace
  ) => {
    return () => {
      if (!flashcard.id) {
        remove(flashcardIndex);
        return;
      }
      replace(flashcardIndex, {
        ...flashcard,
        _destroy: true
      });
    };
  };

  async function handleUploadSingleImage(e, qIndex, type) {
    const file = e?.target?.files[0];

    if (!file) return;

    if (!file.type.includes("image")) {
      dispatch(
        showHideSnackbar({
          type: "error",
          isOpen: true,
          message: "Please select image"
        })
      );
      return;
    }

    const url = URL.createObjectURL(file);
    const objFile = { file, url };

    if (type === "question") {
      const current =
        formRef.current.values.flashcards[qIndex]
          .flashcard_attachments_attributes || [];

      // max 4 images guard
      if (current.length >= 4) return;

      formRef.current.setFieldValue(
        `flashcards[${qIndex}].flashcard_attachments_attributes`,
        [...current, { path: objFile, position: current.length }] // path holds { file, url } until S3 upload
      );

      if (uploadQuestionRef.current)
        uploadQuestionRef.current.value = null;
    } else if (type === "justification") {
      const current =
        formRef.current.values.flashcards[qIndex]
          .flashcard_explanation_attributes
          .flashcard_attachments_attributes || [];

      if (current.length >= 4) return;

      formRef.current.setFieldValue(
        `flashcards[${qIndex}].flashcard_explanation_attributes.flashcard_attachments_attributes`,
        [...current, { path: objFile, position: current.length }] // explanation stays single image
      );

      if (uploadJustificationRef.current)
        uploadJustificationRef.current.value = null;
    }

    e.target.value = null;
  }
  return (
    <div>
      <div className="mb-4">
        <Typography variant="h4" component="h1">
          {mode === "Edit"
            ? "Edit FlashCard Question"
            : isViewMode
            ? "View FlashCard Question"
            : "Add FlashCard Question"}
        </Typography>
        {imported && (
          <Alert severity="info" className="mt-3">
            Imported {imported.flashcards.length} question
            {imported.flashcards.length === 1 ? "" : "s"} from{" "}
            <strong>{imported.fileName}</strong>. Review every
            question, set Difficulty / Mode / System / Subject, then
            submit.
          </Alert>
        )}
        <Formik
          innerRef={formRef}
          initialValues={
            mode === "Add"
              ? imported?.flashcards?.length
                ? { flashcards: imported.flashcards }
                : getInitialValuesAdd()
              : getInitialValuesEdit(flashcard)
          }
          onSubmit={handleSubmit}
          validationSchema={
            isViewMode ? null : ADD_EDIT_FLASHCARD_SCHEMA()
          }
          enableReinitialize={isNotAddMode}
        >
          {({ values, errors, setFieldValue }) => {
            console.log("errors", errors);
            console.log("values", values);
            return (
              <Form>
                <fieldset>
                  <FieldArray name="flashcards">
                    {({ push, remove, replace }) => {
                      return !values?.flashcards?.length
                        ? null
                        : values?.flashcards?.map(
                            (flashcard, flashcardIndex) => {
                              const isSubTopicDisable =
                                !!flashcard.flashcard_topic_id;
                              return (
                                <div
                                  key={flashcardIndex}
                                  className="d-flex"
                                >
                                  <Paper
                                    style={{ padding: "2rem" }}
                                    className="row align-items-center justify-content-center flex-fill mb-4"
                                  >
                                    {isViewMode && (
                                      <Input
                                        isDisabled={isViewMode}
                                        isRequired={true}
                                        name={`flashcards[${flashcardIndex}].question_code`}
                                        label={questionCodeLabel}
                                        type="text"
                                      />
                                    )}
                                    <CustomSelect
                                      disabled={isViewMode}
                                      name={`flashcards[${flashcardIndex}].difficulty_level`}
                                      options={DIFFIECULTY_LEVEL_LIST.map(
                                        (topic) =>
                                          formatOptions(
                                            topic,
                                            "id",
                                            "name"
                                          )
                                      )}
                                      bgColor={"#ecf1fb"}
                                      placeholder={"Difficulty Level"}
                                      isRequired={true}
                                    />{" "}
                                    <CustomSelect
                                      disabled={isViewMode}
                                      name={`flashcards[${flashcardIndex}].mode_eligibility`}
                                      options={MODE_ELIGIBILITY.map(
                                        (topic) =>
                                          formatOptions(
                                            topic,
                                            "id",
                                            "name"
                                          )
                                      )}
                                      bgColor={"#ecf1fb"}
                                      placeholder={"Select Mode"}
                                      isRequired={true}
                                      handleSelectChange={(value) => {
                                        const options =
                                          flashcard.flashcard_options_attributes ||
                                          [];
                                        const active = options.filter(
                                          (o) => !o._destroy
                                        );
                                        if (value === "memory_only") {
                                          const keep =
                                            active.find((o) =>
                                              Boolean(o.correct)
                                            ) || active[0];
                                          const extras =
                                            active.filter(
                                              (o) => o !== keep
                                            );
                                          const next = [
                                            ...(keep
                                              ? [
                                                  {
                                                    ...keep,
                                                    correct: true
                                                  }
                                                ]
                                              : []),
                                            ...extras
                                              .filter((o) => o.id)
                                              .map((o) => ({
                                                id: o.id,
                                                _destroy: true
                                              }))
                                          ];
                                          if (!keep)
                                            next.push(
                                              flashCardInitialObj
                                                .flashcard_options_attributes[0]
                                            );
                                          setFieldValue(
                                            `flashcards[${flashcardIndex}].flashcard_options_attributes`,
                                            next
                                          );
                                        } else if (
                                          active.length < 2
                                        ) {
                                          const next = [...active];
                                          while (next.length < 2) {
                                            next.push({
                                              content: "",
                                              correct: false,
                                              flashcard_attachments_attributes:
                                                [],
                                              currentTab:
                                                next[0]?.currentTab ||
                                                "text"
                                            });
                                          }
                                          setFieldValue(
                                            `flashcards[${flashcardIndex}].flashcard_options_attributes`,
                                            next
                                          );
                                        }
                                      }}
                                    />
                                    <InfiniteScrollSelect
                                      name={`flashcards[${flashcardIndex}].flashcard_topic_id`}
                                      items={list ?? []}
                                      hasMore={topicsHasMore}
                                      placeholder={"System"}
                                      bgColor={"#ecf1fb"}
                                      isRequired={true}
                                      isMenuRelative={true}
                                      disabled={isViewMode}
                                      onSearch={(val) => {
                                        topicsSearchRef.current = val;
                                        if (!val) {
                                          dispatch(
                                            getAllFlashCardTopicsRequest()
                                          );
                                        } else {
                                          dispatch(
                                            getAllFlashCardTopicsRequest(
                                              {
                                                page: 1,
                                                search: val,
                                                items: 10,
                                                paginate: true
                                              }
                                            )
                                          );
                                        }
                                      }}
                                      onLoadMore={() => {
                                        const nextPage =
                                          (topicsMeta?.currentPage ||
                                            0) + 1;
                                        dispatch(
                                          getAllFlashCardTopicsRequest(
                                            {
                                              page: nextPage,
                                              search:
                                                topicsSearchRef.current,
                                              items: 10,
                                              paginate: true
                                            }
                                          )
                                        );
                                      }}
                                      onChange={(selectedId) => {
                                        setFieldValue(
                                          `flashcards[${flashcardIndex}].flashcard_sub_topic_id`,
                                          null
                                        );
                                        if (
                                          selectedId &&
                                          !subTopicsCache[selectedId]
                                        ) {
                                          dispatch(
                                            getAllFlashCardSubTopicsRequest(
                                              {
                                                topicId: selectedId,
                                                page: 1,
                                                items: 10,
                                                paginate: true
                                              }
                                            )
                                          );
                                        }
                                      }}
                                    />
                                    <InfiniteScrollSelect
                                      name={`flashcards[${flashcardIndex}].flashcard_sub_topic_id`}
                                      items={
                                        subTopicsCache[
                                          flashcard.flashcard_topic_id
                                        ]?.items ?? []
                                      }
                                      hasMore={
                                        subTopicsCache[
                                          flashcard.flashcard_topic_id
                                        ]?.hasMore ?? false
                                      }
                                      placeholder="Subject"
                                      bgColor="#ecf1fb"
                                      isRequired={true}
                                      isMenuRelative={true}
                                      disabled={
                                        !isSubTopicDisable ||
                                        isViewMode
                                      }
                                      onSearch={(val) => {
                                        const topicId =
                                          flashcard.flashcard_topic_id;
                                        if (!topicId) return;
                                        setSubTopicsCache((prev) => ({
                                          ...prev,
                                          [topicId]: {
                                            ...(prev[topicId] || {}),
                                            search: val
                                          }
                                        }));
                                        dispatch(
                                          getAllFlashCardSubTopicsRequest(
                                            {
                                              topicId,
                                              page: 1,
                                              search: val,
                                              items: 10,
                                              paginate: true
                                            }
                                          )
                                        );
                                      }}
                                      onLoadMore={() => {
                                        const topicId =
                                          flashcard.flashcard_topic_id;
                                        if (!topicId) return;
                                        const cached =
                                          subTopicsCache[topicId];
                                        const nextPage =
                                          (cached?.page || 1) + 1;
                                        dispatch(
                                          getAllFlashCardSubTopicsRequest(
                                            {
                                              topicId,
                                              page: nextPage,
                                              search:
                                                cached?.search || "",
                                              items: 10,
                                              paginate: true
                                            }
                                          )
                                        );
                                      }}
                                    />
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div
                                        style={{
                                          width: "100%"
                                        }}
                                      >
                                        <Input
                                          inputShape={"textarea"}
                                          numOfRows={2}
                                          isDisabled={isViewMode}
                                          name={`flashcards[${flashcardIndex}].question_body`}
                                          label={questionLabel}
                                          isRequired={true}
                                          type="text"
                                        />
                                      </div>

                                      {mode !== "View" && (
                                        <a
                                          data-bs-toggle="collapse"
                                          href={`#collapseExample${flashcardIndex}`}
                                          role="button"
                                          aria-expanded="false"
                                          aria-controls="collapseExample"
                                          style={{
                                            width: "100px",
                                            marginLeft: "10px"
                                          }}
                                          className="mb-4"
                                        >
                                          {"Upload Image"}
                                        </a>
                                      )}
                                    </div>
                                    <div
                                      className={
                                        values?.flashcards?.[
                                          flashcardIndex
                                        ]
                                          ?.flashcard_attachments_attributes
                                          ?.length
                                          ? "collapse show p-2 mb-2"
                                          : "collapse p-2 mb-2"
                                      }
                                      id={`collapseExample${flashcardIndex}`}
                                    >
                                      <div className="d-flex align-items-center justify-content-between">
                                        <Grid
                                          item
                                          className="pb-0"
                                          xs={6}
                                        >
                                          <div className="text-center">
                                            <MaterialPreviewCarousel
                                              flashcardIndex={
                                                flashcardIndex
                                              }
                                              fieldPath={`flashcards[${flashcardIndex}].flashcard_attachments_attributes`}
                                              type={
                                                uploadType.question
                                              }
                                              attachments={
                                                values?.flashcards?.[
                                                  flashcardIndex
                                                ]
                                                  ?.flashcard_attachments_attributes
                                              }
                                              setFieldValue={
                                                setFieldValue
                                              }
                                              handleUploadSingleImage={
                                                handleUploadSingleImage
                                              }
                                              uploadRef={
                                                uploadQuestionRef
                                              }
                                              uploadImageLabel={
                                                uploadImageLabel
                                              }
                                              mode={mode}
                                            />
                                          </div>
                                        </Grid>
                                      </div>
                                    </div>
                                    {isViewMode ? (
                                      <>
                                        <FlashCardAnswerView
                                          isViewMode={isViewMode}
                                          values={values}
                                          flashcard={flashcard}
                                          flashcardIndex={
                                            flashcardIndex
                                          }
                                        />
                                      </>
                                    ) : (
                                      <FlashCardAnswerForm
                                        flashcard={flashcard}
                                        flashcardIndex={
                                          flashcardIndex
                                        }
                                        setFieldValue={setFieldValue}
                                        mode={mode}
                                        values={values}
                                        formRef={formRef}
                                      />
                                    )}
                                    {typeof errors?.flashcards?.[
                                      flashcardIndex
                                    ]
                                      ?.flashcard_options_attributes ===
                                      "string" && (
                                      <div
                                        style={{
                                          color: "#b70020",
                                          fontSize: "0.9rem",
                                          fontWeight: "lighter",
                                          marginBottom: "14px"
                                        }}
                                      >
                                        {
                                          errors.flashcards[
                                            flashcardIndex
                                          ]
                                            .flashcard_options_attributes
                                        }
                                      </div>
                                    )}
                                    <div className={`my-2`}>
                                      <Input
                                        inputShape={"textarea"}
                                        isDisabled={isViewMode}
                                        name={`flashcards[${flashcardIndex}].flashcard_explanation_attributes.body`}
                                        label={explanationLabel}
                                        isRequired={true}
                                        numOfRows={4}
                                      />

                                      <div className="w-50 text-center">
                                        <MaterialPreviewCarousel
                                          flashcardIndex={
                                            flashcardIndex
                                          }
                                          fieldPath={`flashcards[${flashcardIndex}].flashcard_explanation_attributes.flashcard_attachments_attributes`}
                                          type={
                                            uploadType.justification
                                          }
                                          attachments={
                                            values?.flashcards?.[
                                              flashcardIndex
                                            ]
                                              ?.flashcard_explanation_attributes
                                              ?.flashcard_attachments_attributes
                                          }
                                          setFieldValue={
                                            setFieldValue
                                          }
                                          handleUploadSingleImage={
                                            handleUploadSingleImage
                                          }
                                          uploadRef={
                                            uploadJustificationRef
                                          }
                                          uploadImageLabel={
                                            uploadImageLabel
                                          }
                                          mode={mode}
                                        />
                                      </div>
                                    </div>
                                  </Paper>
                                  {!isNotAddMode && (
                                    <div
                                      className="d-flex flex-row mb-4  align-items-center mx-5"
                                      key={flashcardIndex}
                                    >
                                      {values.flashcards.length > 1 &&
                                        !flashcard?._destroy &&
                                        values.flashcards.filter(
                                          (x) => !x._destroy
                                        ).length !== 1 && (
                                          <div className="mx-2 mb-3">
                                            <RemoveIcon
                                              onClick={RemoveFlashcard(
                                                flashcard,
                                                remove,
                                                flashcardIndex,
                                                replace
                                              )}
                                              className="cursor-pointer"
                                              disabled={
                                                values.flashcards.filter(
                                                  (x) => !x._destroy
                                                ).length === 1
                                              }
                                            />
                                          </div>
                                        )}
                                      {AddRemoveNewQuestion(push)}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                          );
                    }}
                  </FieldArray>
                </fieldset>
                {mode !== "View" && (
                  <div className="d-block mt-3 ">
                    <Button
                      text={submit}
                      className="primary-lg"
                      type="submit"
                      data-toggle="tooltip"
                      data-placement="top"
                    />
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
}

export default FlashCardForm;
